const TOTAL_ATTEMPTS = 3;
const BASE_DELAY_MS = 250;

interface RetryFileOperationOptions {
  delay?: (milliseconds: number) => Promise<void>;
  random?: () => number;
}

const wait = async (milliseconds: number): Promise<void> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  if ('response' in error) {
    const { response } = error;
    if (
      typeof response === 'object' &&
      response !== null &&
      'status' in response &&
      typeof response.status === 'number'
    ) {
      return response.status;
    }
  }

  if (
    'data' in error &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'httpStatus' in error.data &&
    typeof error.data.httpStatus === 'number'
  ) {
    return error.data.httpStatus;
  }

  if (
    'shape' in error &&
    typeof error.shape === 'object' &&
    error.shape !== null &&
    'data' in error.shape &&
    typeof error.shape.data === 'object' &&
    error.shape.data !== null &&
    'httpStatus' in error.shape.data &&
    typeof error.shape.data.httpStatus === 'number'
  ) {
    return error.shape.data.httpStatus;
  }

  return undefined;
};

const getCode = (value: unknown): string | undefined => {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('code' in value) ||
    typeof value.code !== 'string'
  ) {
    return undefined;
  }

  return value.code;
};

const getTrpcCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  if ('data' in error) {
    const code = getCode(error.data);
    if (code) {
      return code;
    }
  }

  if ('shape' in error && typeof error.shape === 'object' && error.shape) {
    return 'data' in error.shape ? getCode(error.shape.data) : undefined;
  }

  return undefined;
};

const NON_RETRYABLE_CODES = new Set([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'BAD_USER_INPUT',
  'UNAUTHENTICATED',
  'validation-failed',
  'permission-error',
  'constraint-violation',
]);

const hasNonRetryableGraphqlError = (error: unknown): boolean => {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('graphQLErrors' in error) ||
    !Array.isArray(error.graphQLErrors)
  ) {
    return false;
  }

  return error.graphQLErrors.some((graphQLError) => {
    if (
      typeof graphQLError !== 'object' ||
      graphQLError === null ||
      !('extensions' in graphQLError)
    ) {
      return false;
    }

    return NON_RETRYABLE_CODES.has(getCode(graphQLError.extensions) ?? '');
  });
};

const isRetryable = (error: unknown): boolean => {
  const status = getHttpStatus(error);
  const trpcCode = getTrpcCode(error);

  if (
    (trpcCode && NON_RETRYABLE_CODES.has(trpcCode)) ||
    hasNonRetryableGraphqlError(error)
  ) {
    return false;
  }

  // A file-operation 409 is a terminal relation/state conflict. Entity-create
  // idempotency treats 409 differently because it can mean a concurrent claim
  // is still completing; this retry primitive never owns that create claim.
  return (
    status === undefined || status === 408 || status === 429 || status >= 500
  );
};

/** Retry one file persistence operation up to three times without overlap. */
export const retryFileOperation = async <T>(
  operation: () => Promise<T>,
  { delay = wait, random = Math.random }: RetryFileOperationOptions = {}
): Promise<T> => {
  for (let attempt = 1; attempt <= TOTAL_ATTEMPTS; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === TOTAL_ATTEMPTS || !isRetryable(error)) {
        throw error;
      }

      const exponentialDelay = BASE_DELAY_MS * 2 ** (attempt - 1);
      const jitterMultiplier = 0.5 + random();
      await delay(exponentialDelay * jitterMultiplier);
    }
  }

  throw new Error('File operation exhausted its retry budget');
};
